import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFile,
  faFileVideo,
  faImage,
  faStickyNote,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import type { SelectedEvidenceItem } from '@/services/authorization-case.service';
import { LightboxViewer, type LightboxImage } from '@/components/shared/LightboxViewer';
import { fmtDateTime } from '../utils';

interface Props {
  selectedEvidence: SelectedEvidenceItem[] | undefined;
}

const PHOTO_TYPES = new Set(['PHOTO', 'IMAGE']);
const VIDEO_TYPES = new Set(['VIDEO']);
const DOCUMENT_TYPES = new Set(['DOCUMENT']);

export function EvidenceGallery({ selectedEvidence }: Props) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const items = selectedEvidence ?? [];
  const photos = items.filter((i) => i.mediaType && PHOTO_TYPES.has(i.mediaType) && i.url);
  const videos = items.filter((i) => i.mediaType && VIDEO_TYPES.has(i.mediaType));
  const documents = items.filter((i) => i.mediaType && DOCUMENT_TYPES.has(i.mediaType));
  const findings = items.filter((i) => i.sourceType === 'FINDING');
  const notes = items.filter((i) => i.sourceType === 'NOTE');

  const lightboxImages: LightboxImage[] = photos.map((p) => ({
    id: p.id,
    url: p.url as string,
    filename: p.label,
  }));

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-5 py-3.5">
        <h3 className="text-sm font-semibold text-foreground">Evidence selected by Engineer</h3>
      </div>
      <div className="px-5 py-4">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            The Engineer did not select any supporting evidence for this request.
          </p>
        ) : (
          <div className="space-y-4">
            {photos.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  Photos ({photos.length})
                </p>
                <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
                  {photos.map((photo) => {
                    const idx = lightboxImages.findIndex((li) => li.id === photo.id);
                    return (
                      <button
                        key={photo.id}
                        className="aspect-square overflow-hidden rounded-md bg-muted"
                        onClick={() => {
                          if (idx >= 0) {
                            setLightboxIndex(idx);
                            setLightboxOpen(true);
                          }
                        }}
                        aria-label={`View ${photo.label}`}
                      >
                        {photo.thumbnailUrl || photo.url ? (
                          <img
                            src={photo.thumbnailUrl ?? photo.url ?? ''}
                            alt={photo.label}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <FontAwesomeIcon
                              icon={faImage}
                              className="h-4 w-4 text-muted-foreground/40"
                            />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <LightboxViewer
                  images={lightboxImages}
                  initialIndex={lightboxIndex}
                  open={lightboxOpen}
                  onClose={() => setLightboxOpen(false)}
                />
              </div>
            )}

            {videos.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  Videos ({videos.length})
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {videos.map((v) => (
                    <a
                      key={v.id}
                      href={v.url ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="flex aspect-square flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/30 p-2 text-center transition hover:bg-muted/60"
                    >
                      <FontAwesomeIcon
                        icon={faFileVideo}
                        className="h-5 w-5 text-muted-foreground"
                      />
                      <span className="w-full truncate text-[10px] text-muted-foreground">
                        {v.label}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {documents.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  Documents ({documents.length})
                </p>
                <div className="space-y-1.5">
                  {documents.map((d) => (
                    <a
                      key={d.id}
                      href={d.url ?? undefined}
                      download={d.label}
                      className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm transition hover:bg-muted"
                    >
                      <FontAwesomeIcon
                        icon={faFile}
                        className="h-3.5 w-3.5 text-muted-foreground"
                      />
                      <span className="flex-1 truncate text-foreground">{d.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {findings.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  Findings ({findings.length})
                </p>
                <div className="space-y-1.5">
                  {findings.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <FontAwesomeIcon
                        icon={faWrench}
                        className="h-3.5 w-3.5 text-muted-foreground"
                      />
                      <span className="flex-1 text-foreground">{f.label}</span>
                      {f.findingSeverity && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          {f.findingSeverity}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {notes.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  Notes ({notes.length})
                </p>
                <div className="space-y-1.5">
                  {notes.map((n) => (
                    <div
                      key={n.id}
                      className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <FontAwesomeIcon
                        icon={faStickyNote}
                        className="mt-0.5 h-3.5 w-3.5 text-muted-foreground"
                      />
                      <span className="flex-1 text-foreground">{n.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Selected by {items[0]?.selectedByName} · {fmtDateTime(items[0]?.createdAt ?? '')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
